import {ProjectMainPathologiesForm} from '@/features/projects/components/project-main-pathologies-form/ProjectMainPathologiesForm'
import {ProjectPathologiesDetails} from '@/features/projects/components/project-pathologies-details/ProjectPathologiesDetails'
import {useGetProjectPathologies} from '@/features/projects/queries/useGetProjectPathologies'
import {CardInEditModeType} from '@/pages/project-details-diagnoses-facilities/ProjectDetailsDiagnosesFacilities'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {Dispatch, SetStateAction, useCallback, useMemo} from 'react'

interface ProjectMainPathologiesSectionProps {
    projectId: string
    editModeState: [CardInEditModeType, Dispatch<SetStateAction<CardInEditModeType>>]
}

export const ProjectMainPathologiesSection = ({projectId, editModeState}: ProjectMainPathologiesSectionProps) => {
    const {t} = useTranslation()
    const [cardInEditMode, setCardInEditMode] = editModeState

    const {
        data: projectPathologies,
        isLoading,
        isError
    } = useGetProjectPathologies({
        urlParams: {projectId}
    })

    const handleCancel = useCallback(() => {
        setCardInEditMode(null)
    }, [setCardInEditMode])

    const handleEdit = useCallback(() => {
        setCardInEditMode('ProjectMainPathologiesSection')
    }, [setCardInEditMode])

    const isEditModeActive = useMemo(() => cardInEditMode == 'ProjectMainPathologiesSection', [cardInEditMode])
    const isDisabled = useMemo(
        () => !!(cardInEditMode && cardInEditMode != 'ProjectMainPathologiesSection'),
        [cardInEditMode]
    )

    const pathologiesDefaultValues = useMemo(() => {
        return projectPathologies && projectPathologies?.length > 0
            ? projectPathologies.map(projectPathology => ({
                  diagnosisId: projectPathology.diagnosisId,
                  startedAt: dayjs(projectPathology.startedAt).format('YYYY-MM-DD'),
                  description: projectPathology.pathology.description,
                  code: projectPathology.pathology.code
              }))
            : undefined
    }, [projectPathologies])

    if (isEditModeActive) {
        return (
            <ProjectMainPathologiesForm
                projectId={projectId}
                isDisabled={isDisabled}
                isEditModeActive={isEditModeActive}
                defaultValues={pathologiesDefaultValues}
                handleCancel={handleCancel}
                handleEdit={handleEdit}
            />
        )
    }

    return (
        <ProjectPathologiesDetails
            pathologies={projectPathologies}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isError={isError}
            isActive={isEditModeActive}
            title={t('projects:rehab_pathology_card_title')}
            handleCancel={handleCancel}
            handleEdit={handleEdit}
            showStartDate
            isMainPathologies
        />
    )
}
